import { Component, OnInit, OnDestroy, inject, signal, computed, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent, MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApplicationItem } from '../../../quotas/models/application.interface';
import { QuotaService } from '../../../quotas/services/quota.service';
import { AuthService } from '../../../../core/services/auth.service';
import { KgToTonsPipe } from '../../../../shared/pipes/kg-to-tons.pipe';
import { StatusLabelPipe } from '../../../../shared/pipes/status-label.pipe';
import { StatusClassPipe } from '../../../../shared/pipes/status-class.pipe';
import { SupplierTypePipe } from '../../../../shared/pipes/supplier-type.pipe';
import { VetDocStatusPipe } from '../../../../shared/pipes/vet-doc-status.pipe';
import { QuotaStatusPipe } from '../../../../shared/pipes/quota-status.pipe';
import { getRussianPaginatorIntl } from '../../../../core/providers/russian-paginator-intl';
import { StatusFilterComponent } from '../../components/status-filter/status-filter';
import { PaginationService } from '../../../../core/services/pagination.service';
import { APPLICATION_STATUSES, MEAT_TYPES, SUPPLIER_TYPES } from '../../../../core/constants/app.constants';
import { QuotaSummaryBarComponent } from '../../../../shared/components/quota-summary-bar/quota-summary-bar';
import { VrmsxDialogComponent } from '../../components/vrmsx-dialog/vrmsx-dialog.component';
import { ModerateDialogComponent } from '../../components/moderate-dialog/moderate-dialog.component';
import { HistoricalVolumesDialogComponent } from '../../components/historical-volumes-dialog/historical-volumes-dialog.component';
import { ConsiderationDialogComponent } from '../../components/consideration-dialog/consideration-dialog.component';

@Component({
  selector: 'app-applications-register',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
    MatMenuModule,
    MatDialogModule,
    MatSnackBarModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    FormsModule,
    KgToTonsPipe,
    StatusLabelPipe,
    StatusClassPipe,
    SupplierTypePipe,
    VetDocStatusPipe,
    QuotaStatusPipe,
    StatusFilterComponent,
    QuotaSummaryBarComponent,
    TranslateModule
  ],
  providers: [
    { provide: MatPaginatorIntl, useValue: getRussianPaginatorIntl() }
  ],
  templateUrl: './applications-register.component.html',
  styleUrls: ['./applications-register.component.css']
})
export class ApplicationsRegisterComponent implements OnInit, OnDestroy {
  private quotaService = inject(QuotaService);
  private authService = inject(AuthService);
  private paginationService = inject(PaginationService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);

  // Данные
  applications = signal<ApplicationItem[]>([]);
  loading = signal(true);
  filtering = signal(false);
  error = signal<string | null>(null);
  private initialLoad = true;

  // Проверка роли - quota_manager может только просматривать
  isManager = computed(() => {
    return this.authService.hasRole('quota_viewer') || (this.authService.hasRole('quota_manager') && !this.authService.hasRole('quota_operator'));
  });

  // Пагинация
  pagination = this.paginationService.createPaginationState(15);

  // Фильтры
  statusFilter = '';
  searchQuery = '';
  openFilterColumn = '';

  // Debounce для фильтров
  private filterSubject = new Subject<void>();

  // Расширенные фильтры
  filters = {
    companyInn: '',
    companyName: '',
    registrationNumber: '',
    proxyDocumentNumber: '',
    vetServiceIssueNumber: '',
    namePatronymic: '',
    passportNumber: '',
    phoneNumber: '',
    email: '',
    supplierType: '',
    meatTypeId: null as number | null,
    requestedVolumeFrom: null as number | null,
    requestedVolumeTo: null as number | null,
    year: null as number | null,
    contractDateFrom: null as Date | null,
    contractDateTo: null as Date | null
  };

  // Типы мяса для фильтра
  meatTypes = MEAT_TYPES;

  // Типы поставщиков
  supplierTypes = SUPPLIER_TYPES;

  // Счётчики статусов
  statusCounts = signal<Record<string, number>>({});

  // Колонки таблицы
  displayedColumns = computed(() => {
    const columns = [
      'id',
      'applicationNumber',
      'companyName',
      'companyInn',
      'proxyDocumentNumber',
      'proxyDocumentDate',
      'vetServiceIssueDate',
      'vetServiceIssueNumber',
      'applicantFullName',
      'applicantPassportNumber',
      'applicantPhoneNumber',
      'applicantEmail',
      'meatType',
      'requestedVolume',
      'volumeForLastNineMonths',
      'volumeForLastTwelveMonths',
      'supplierType',
      'veterinaryDocumentStatus',
      'quotaStatus',
      'applicationStatus'
    ];

    // Добавляем колонку действий только если НЕ менеджер
    if (!this.isManager()) {
      columns.push('actions');
    }

    return columns;
  });

  // Статусы для фильтра
  statuses = APPLICATION_STATUSES;

  ngOnInit(): void {
    this.loadStatusCounts();
    this.loadApplications();

    // Подписка на debounced фильтры
    this.filterSubject.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.applyFilters();
    });
  }

  ngOnDestroy(): void {
    this.filterSubject.complete();
  }

  loadStatusCounts(): void {
    this.quotaService.getAllApplications({ page: 1, size: 1000 }).subscribe({
      next: (response) => {
        if (response.success) {
          const apps = response.res.content || [];
          const counts: Record<string, number> = { '': apps.length };

          apps.forEach((app: any) => {
            counts[app.applicationStatus] = (counts[app.applicationStatus] || 0) + 1;
          });

          this.statusCounts.set(counts);
        }
      }
    });
  }

  setStatusFilter(status: string): void {
    this.statusFilter = status;
    this.pagination.reset();
    this.loadApplications();
  }

  toggleColumnFilter(column: string): void {
    console.log('Before toggle:', this.openFilterColumn);
    if (this.openFilterColumn === column) {
      this.openFilterColumn = '';
    } else {
      this.openFilterColumn = column;
    }
    console.log('After toggle:', this.openFilterColumn);
    this.cdr.detectChanges();
  }

  applyFilters(): void {
    this.pagination.reset();
    this.loadApplications();
  }

  onFilterChange(): void {
    this.filterSubject.next();
  }

  clearFilter(filterName: string): void {
    if (filterName === 'meatTypeId' || filterName === 'contractDateFrom' || filterName === 'contractDateTo') {
      (this.filters as any)[filterName] = null;
    } else {
      (this.filters as any)[filterName] = '';
    }
    this.onFilterChange();
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  resetFilters(): void {
    this.filters = {
      companyInn: '',
      companyName: '',
      registrationNumber: '',
      proxyDocumentNumber: '',
      vetServiceIssueNumber: '',
      namePatronymic: '',
      passportNumber: '',
      phoneNumber: '',
      email: '',
      supplierType: '',
      meatTypeId: null,
      requestedVolumeFrom: null,
      requestedVolumeTo: null,
      year: null,
      contractDateFrom: null,
      contractDateTo: null
    };
    this.pagination.reset();
    this.loadApplications();
  }

  loadApplications(): void {
    if (this.initialLoad) {
      this.loading.set(true);
    } else {
      this.filtering.set(true);
    }
    this.error.set(null);

    const params: any = this.pagination.getParams();

    if (this.statusFilter) {
      params.status = this.statusFilter;
    }

    // Добавляем расширенные фильтры
    if (this.filters.companyInn) params.companyInn = this.filters.companyInn;
    if (this.filters.companyName) params.companyName = this.filters.companyName;
    if (this.filters.registrationNumber) params.registrationNumber = this.filters.registrationNumber;
    if (this.filters.proxyDocumentNumber) params.proxyDocumentNumber = this.filters.proxyDocumentNumber;
    if (this.filters.vetServiceIssueNumber) params.vetServiceIssueNumber = this.filters.vetServiceIssueNumber;
    if (this.filters.namePatronymic) params.namePatronymic = this.filters.namePatronymic;
    if (this.filters.passportNumber) params.passportNumber = this.filters.passportNumber;
    if (this.filters.phoneNumber) params.phoneNumber = this.filters.phoneNumber;
    if (this.filters.email) params.email = this.filters.email;
    if (this.filters.supplierType) params.supplierType = this.filters.supplierType;
    if (this.filters.meatTypeId) params.meatTypeId = this.filters.meatTypeId;
    if (this.filters.requestedVolumeFrom) params.requestedVolumeFrom = this.filters.requestedVolumeFrom;
    if (this.filters.requestedVolumeTo) params.requestedVolumeTo = this.filters.requestedVolumeTo;
    if (this.filters.year) params.year = this.filters.year;
    if (this.filters.contractDateFrom) params.contractDateFrom = this.formatDate(this.filters.contractDateFrom);
    if (this.filters.contractDateTo) params.contractDateTo = this.formatDate(this.filters.contractDateTo);

    this.quotaService.getAllApplications(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.applications.set(response.res.content || []);
          this.pagination.setTotalElements(response.res.totalElements || 0);
        } else {
          this.error.set(response.msg || 'Ошибка загрузки данных');
        }
        this.loading.set(false);
        this.filtering.set(false);
        this.initialLoad = false;
      },
      error: (err) => {
        console.error('Error loading applications:', err);
        this.error.set('Не удалось загрузить заявки');
        this.loading.set(false);
        this.filtering.set(false);
        this.initialLoad = false;
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.pagination.onPageChange(event);
    this.loadApplications();
  }

  onStatusFilterChange(): void {
    this.pagination.reset();
    this.loadApplications();
  }

  viewDetails(application: ApplicationItem): void {
    console.log('View details:', application);
    // TODO: Открыть модалку с деталями
  }

  approveApplication(application: ApplicationItem): void {
    const dialogRef = this.dialog.open(ModerateDialogComponent, {
      width: '500px',
      data: { application, action: 'APPROVE' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadApplications();
        this.loadStatusCounts();
      }
    });
  }

  rejectApplication(application: ApplicationItem): void {
    const dialogRef = this.dialog.open(ModerateDialogComponent, {
      width: '500px',
      data: { application, action: 'REJECT' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadApplications();
        this.loadStatusCounts();
      }
    });
  }

  setVrmsx(application: ApplicationItem): void {
    const dialogRef = this.dialog.open(VrmsxDialogComponent, {
      width: '500px',
      data: { application }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadApplications();
        this.loadStatusCounts();
      }
    });
  }

  setLastDeliveries(application: ApplicationItem): void {
    const dialogRef = this.dialog.open(HistoricalVolumesDialogComponent, {
      width: '500px',
      data: {
        application,
        supplierId: application.supplierId || '',
        quotaYear: new Date().getFullYear()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadApplications();
      }
    });
  }

  setUnderConsideration(application: ApplicationItem): void {
    const dialogRef = this.dialog.open(ConsiderationDialogComponent, {
      width: '500px',
      data: { application }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadApplications();
        this.loadStatusCounts();
      }
    });
  }

  downloadPdfReport(application: ApplicationItem): void {
    this.quotaService.exportApplicationPdf(application.id).subscribe({
      next: (blob) => {
        // Проверяем что это PDF
        if (blob.type && !blob.type.includes('pdf')) {
          this.snackBar.open('Ошибка: ответ не является PDF', 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `application-${application.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.snackBar.open('Ошибка скачивания PDF', 'OK', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      }
    });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
